import type { ProjectOnboarding, ProjectTask, TaskGroup } from '../types';
import {
  isOnboardingBridgeTaskId,
  onboardingItemToProjectTask,
  onboardingTaskIdToItemId,
  parseOnboardingBridgeTaskId,
} from './onboardingTaskBridge';

export type PendingCompletionTask = {
  taskId: string;
  title: string;
  scheduledStart: Date;
  scheduledEnd: Date;
  projectId: string;
  isOnboarding: boolean;
};

type CompletionPromptContext = {
  projectTasks: ProjectTask[];
  taskGroups: TaskGroup[];
  projectOnboardings?: ProjectOnboarding[];
  getOnboardingForProject?: (projectId: string) => ProjectOnboarding | undefined;
  /** Fallback project id when resolving onboarding bridge tasks. */
  fallbackProjectId?: string;
};

function resolveOnboardingProjectId(
  taskId: string,
  projectOnboardings: ProjectOnboarding[] | undefined,
  fallbackProjectId?: string,
): string | null {
  const parsed = parseOnboardingBridgeTaskId(taskId);
  if (parsed?.projectId) return parsed.projectId;
  const itemId = onboardingTaskIdToItemId(taskId);
  const ob = projectOnboardings?.find((o) => o.items.some((it) => it.id === itemId));
  return ob?.projectId ?? fallbackProjectId ?? null;
}

/**
 * When `updates` marks a task completed, return context for CompleteTaskActualsModal
 * instead of applying immediately. Returns null when completion should proceed normally.
 */
export function resolveTaskCompletionPrompt(
  taskId: string,
  updates: Partial<ProjectTask>,
  ctx: CompletionPromptContext,
): PendingCompletionTask | null {
  if (updates.status !== 'completed') return null;
  if ((updates as Record<string, unknown>)._skipCompletionPrompt) return null;

  if (isOnboardingBridgeTaskId(taskId)) {
    const projectId = resolveOnboardingProjectId(
      taskId,
      ctx.projectOnboardings,
      ctx.fallbackProjectId,
    );
    if (!projectId || !ctx.getOnboardingForProject) return null;
    const itemId = onboardingTaskIdToItemId(taskId);
    const obItem = ctx.getOnboardingForProject(projectId)?.items?.find((it) => it.id === itemId);
    if (!obItem || obItem.taskStatus === 'completed' || obItem.completed) return null;
    const groupsForProject = ctx.taskGroups.filter((g) => g.projectId === projectId);
    const sched = onboardingItemToProjectTask(obItem, projectId, groupsForProject);
    return {
      taskId,
      title: obItem.title,
      scheduledStart: sched.startDate,
      scheduledEnd: sched.endDate,
      projectId,
      isOnboarding: true,
    };
  }

  const task = ctx.projectTasks.find((t) => t.id === taskId);
  if (!task || task.status === 'completed' || !task.projectId) return null;
  return {
    taskId,
    title: task.title,
    scheduledStart: new Date(task.startDate),
    scheduledEnd: new Date(task.endDate),
    projectId: task.projectId,
    isOnboarding: false,
  };
}

type ApplyTaskCompletionDeps = {
  actorName: string;
  updateProjectTask: (id: string, updates: Partial<ProjectTask>) => Promise<void>;
  updateOnboardingItem: (
    projectId: string,
    itemId: string,
    updates: Record<string, unknown>,
    actorName: string,
  ) => Promise<void>;
};

/** Write actual dates, mark complete, and lock the task bar. */
export async function applyTaskCompletion(
  ctx: PendingCompletionTask,
  actualStart: Date,
  actualEnd: Date,
  deps: ApplyTaskCompletionDeps,
): Promise<void> {
  const actor = deps.actorName;
  const lockedAt = new Date();
  if (ctx.isOnboarding) {
    const itemId = onboardingTaskIdToItemId(ctx.taskId);
    await deps.updateOnboardingItem(
      ctx.projectId,
      itemId,
      {
        completed: true,
        taskStatus: 'completed',
        progress: 100,
        startDate: actualStart,
        endDate: actualEnd,
        actualStartDate: actualStart,
        actualEndDate: actualEnd,
        completedAt: lockedAt,
        scheduleLocked: true,
        lockedAt,
        lockedBy: actor,
      },
      actor,
    );
    return;
  }

  await deps.updateProjectTask(ctx.taskId, {
    status: 'completed',
    progress: 100,
    startDate: actualStart,
    endDate: actualEnd,
    actualStartDate: actualStart,
    actualEndDate: actualEnd,
    completedAt: lockedAt,
    scheduleLocked: true,
    lockedAt,
    lockedBy: actor,
  });
}
